import { memo, useMemo } from 'react';

import { useTranslate } from '@/hooks/useTranslate';

// 拖拽控制器组件
const SplitController = memo(
  ({
    splitPos,
    onStartDrag,
    isVisible,
  }: {
    splitPos: number;
    onStartDrag: () => void;
    isVisible: boolean;
  }) => {
    if (!isVisible) return null;

    return (
      <div
        className="z-10 w-fit h-full absolute flex flex-col justify-center items-center translate-x-[-50%]"
        style={{
          left: splitPos,
        }}
      >
        <div className="w-[2px] flex-1 bg-white" />
        <div className="cursor-move" onMouseDown={onStartDrag}>
          <svg
            width="46"
            height="46"
            viewBox="0 0 46 46"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="23"
              cy="23"
              r="22.5"
              fill="#DBDBDB"
              fillOpacity="0.4"
              stroke="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.343 22.9996L17.414 30.0706L16 31.4846L8.22199 23.7066C8.03451 23.5191 7.9292 23.2648 7.9292 22.9996C7.9292 22.7345 8.03451 22.4802 8.22199 22.2926L16 14.5146L17.414 15.9286L10.343 22.9996Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M35.657 23.0004L28.586 15.9294L30 14.5154L37.778 22.2934C37.9655 22.4809 38.0708 22.7352 38.0708 23.0004C38.0708 23.2655 37.9655 23.5198 37.778 23.7074L30 31.4854L28.586 30.0714L35.657 23.0004Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="w-[2px] flex-1 bg-white" />
      </div>
    );
  },
);

// 图片面板组件
const ImagePanel = memo(
  ({
    src,
    clipPath,
    labelText,
    labelStyle,
    onLoad,
    onError,
  }: {
    src: string;
    clipPath: string;
    labelText: string;
    labelStyle: React.CSSProperties;
    onLoad: () => void;
    onError: () => void;
  }) => {
    return (
      <div className="w-full h-full absolute left-0 overflow-hidden" style={{ clipPath }}>
        <div
          className="min-w-[60px] text-[15px] text-[#fff] leading-[15px] p-2 flex justify-center items-center bg-[rgba(0,0,0,0.3)] absolute top-2 z-20 rounded-md border border-[rgba(255,255,255,0.5)]"
          style={labelStyle}
        >
          {labelText}
        </div>
        <img
          src={src}
          className="w-full h-full object-contain"
          alt="comparison image"
          onLoad={onLoad}
          onError={onError}
        />
      </div>
    );
  },
);

const CompareMode = memo(
  ({
    splitPos,
    startDrag,
    isLoading,
    previewContentSize,
    onBeforeImageLoad,
    onAfterImageLoad,
    beforeImgUrl,
    afterImgUrl,
  }: {
    splitPos: number;
    startDrag: () => void;
    isLoading: boolean;
    previewContentSize: { width: number; height: number };
    onBeforeImageLoad: () => void;
    onAfterImageLoad: () => void;
    beforeImgUrl: string;
    afterImgUrl: string;
  }) => {
    const { t } = useTranslate();
    const beforeLabelStyle = useMemo(
      () => ({
        left: Math.min(8, Math.max(8, splitPos - 68)),
      }),
      [splitPos],
    );

    const afterLabelStyle = useMemo(
      () => ({
        right: Math.min(8, Math.max(8, previewContentSize.width - splitPos - 68)),
      }),
      [splitPos, previewContentSize.width],
    );

    const beforeClipPath = useMemo(
      () => `inset(0 calc(100% - ${splitPos}px) 0 0)`,
      [splitPos],
    );

    const afterClipPath = useMemo(() => `inset(0 0 0 ${splitPos}px)`, [splitPos]);

    return (
      <>
        <SplitController
          splitPos={splitPos}
          onStartDrag={startDrag}
          isVisible={!isLoading}
        />

        <ImagePanel
          src={beforeImgUrl}
          clipPath={beforeClipPath}
          labelText={t('Image-Enhance-Before')}
          labelStyle={beforeLabelStyle}
          onLoad={onBeforeImageLoad}
          onError={onBeforeImageLoad}
        />

        <ImagePanel
          src={afterImgUrl}
          clipPath={afterClipPath}
          labelText={t('Image-Enhance-After')}
          labelStyle={afterLabelStyle}
          onLoad={onAfterImageLoad}
          onError={onAfterImageLoad}
        />
      </>
    );
  },
);

export default CompareMode;
